/**
 * AsTooltipWrapper Component
 * 
 * A wrapper component that creates a tooltip attached to its child elements.
 * It supports multiple display modes: show-on-hover, toggle-on-click, or manual control.
 * Uses @floating-ui/dom for robust positioning with collision detection and boundary awareness.
 * 
 * Attributes:
 * - textKey: Localization key for the tooltip text
 * - default-text: Default text to display if no translation is found
 * - show: When present, displays the tooltip
 * - placement: Position of the tooltip (top, bottom, left, right, etc.)
 * - show-on-hover: When present, shows tooltip on hover
 * - toggle-on-click: When present, toggles tooltip visibility on click
 * - no-arrow: When present, hides the tooltip arrow (passed to as-tooltip)
 * - auto-flip: When present, allows the tooltip to flip position when near viewport edges
 * - offset: Distance in pixels from the trigger element (default: 8)
 */
import { passAttributes } from '../wcUtil.ts';
import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom';
import styleText from './as-tooltip-wrapper.css?raw';

export class AsTooltipWrapper extends HTMLElement {
  private cleanup?: () => void;
  private intersectionObserver?: IntersectionObserver;
  private shouldBeVisible: boolean = false;
  private flashTimeout?: number;
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  static get observedAttributes() {
    return ['dict-key', 'default-text', 'show', 'placement', 'show-on-hover', 'toggle-on-click', 'no-arrow', 'offset', 'auto-flip', 'flash', 'flash-duration', 'flash-delay'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsTooltipWrapper.sheet
    ];
  }

  /**
   * Map our placement terms to Floating UI placements
   */
  private mapPlacement(placement: string) {
    const placementMap: { [key: string]: string } = {
      'top': 'top',
      'bottom': 'bottom',
      'left': 'left',
      'right': 'right',
      'top-left': 'top-end',
      'top-right': 'top-start',
      'bottom-left': 'bottom-end',
      'bottom-right': 'bottom-start'
    };
    return placementMap[placement] || 'bottom';
  }

  /**
   * Get offset value
   */
  private getOffset(): number {
    return parseInt(this.getAttribute('offset') ?? '8');
  }

  /**
   * Flash the tooltip - show it briefly then hide it
   */
  private flashTooltip(): void {
    const tooltip = this.shadowRoot?.querySelector('as-tooltip');
    if (!tooltip) return;

    // Clear any existing flash timeout
    if (this.flashTimeout) {
      clearTimeout(this.flashTimeout);
      this.flashTimeout = undefined;
    }

    // Get flash delay (default 0ms) and duration (default 2000ms)
    const delay = parseInt(this.getAttribute('flash-delay') ?? '0');
    const duration = parseInt(this.getAttribute('flash-duration') ?? '2000');

    // Wait for delay, then show the tooltip
    this.flashTimeout = window.setTimeout(() => {
      // Show the tooltip
      this.shouldBeVisible = true;
      tooltip.setAttribute('show', '');
      this.updateTooltipPosition();

      // Hide after duration and remove flash attribute
      this.flashTimeout = window.setTimeout(() => {
        this.shouldBeVisible = false;
        tooltip.removeAttribute('show');

        // Clean up autoUpdate when hiding
        if (this.cleanup) {
          this.cleanup();
          this.cleanup = undefined;
        }

        // Remove the flash attribute to prevent re-triggering
        this.removeAttribute('flash');
        this.flashTimeout = undefined;
      }, duration);
    }, delay);
  }

  /**
   * Set up intersection observer to auto-hide tooltip when scrolled out of view
   */
  private setupIntersectionObserver() {
    const wrapperElement = this.shadowRoot?.querySelector('.tooltip-wrapper') as HTMLElement;
    if (!wrapperElement) return;
    
    // Clean up existing observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const tooltip = this.shadowRoot?.querySelector('as-tooltip');
        if (!tooltip) return;
        
        if (!entry.isIntersecting) {
          // Element is scrolled out of view - hide tooltip if visible
          if (tooltip.hasAttribute('show')) {
            tooltip.removeAttribute('show');
            // Clean up autoUpdate when hiding
            if (this.cleanup) {
              this.cleanup();
              this.cleanup = undefined;
            }
          }
        } else {
          // Element is back in view - restore tooltip if it should be visible
          if (this.shouldBeVisible && !tooltip.hasAttribute('show')) {
            tooltip.setAttribute('show', '');
            this.updateTooltipPosition();
          }
        }
      });
    }, {
      // Use a small margin to hide tooltips slightly before they go completely off-screen
      rootMargin: '-10px'
    });
    
    this.intersectionObserver.observe(wrapperElement);
  }

  /**
   * Update tooltip position using Floating UI
   */
  private updateTooltipPosition() {
    const tooltip = this.shadowRoot?.querySelector('as-tooltip') as HTMLElement;
    const wrapperElement = this.shadowRoot?.querySelector('.tooltip-wrapper') as HTMLElement;
    
    if (!tooltip || !wrapperElement) return;

    const placement = this.getAttribute('placement') || 'bottom';
    const floatingPlacement = this.mapPlacement(placement);
    const offsetValue = this.getOffset();
    
    // Build middleware array conditionally
    const middleware = [offset(offsetValue)];
    
    // Only add flip if auto-flip attribute is present
    if (this.hasAttribute('auto-flip')) {
      middleware.push(flip());
    }
    
    middleware.push(shift({ padding: 5 }));
    
    // Clean up any existing autoUpdate
    if (this.cleanup) {
      this.cleanup();
    }
    
    // Set up intersection observer for auto-hiding
    this.setupIntersectionObserver();
    
    // Set up autoUpdate to handle scroll/resize events
    this.cleanup = autoUpdate(wrapperElement, tooltip, async () => {
      try {
        const { x, y } = await computePosition(wrapperElement, tooltip, {
          placement: floatingPlacement as any,
          strategy: 'fixed', // Use fixed positioning to avoid scroll container clipping
          middleware: middleware,
        });

        // Apply the computed position
        Object.assign(tooltip.style, {
          left: `${x}px`,
          top: `${y}px`,
          transform: 'none', // Reset any transform
          position: 'fixed', // Ensure fixed positioning
        });
      } catch (error) {
        console.warn('Failed to position tooltip:', error);
      }
    });
  }

  /**
   * Set up the component template with a single source of truth approach
   */
  private createTemplate(): DocumentFragment {
    const template = document.createElement('template');
    
    // Pass placement and no-arrow attributes to the tooltip component
    // The tooltip component needs placement for internal arrow positioning
    const tooltipAttributes = ['placement', 'no-arrow'];
    const passedAttributes = passAttributes(this, tooltipAttributes);
    
    template.innerHTML = `
      <div class="tooltip-wrapper" 
           ${this.hasAttribute('show-on-hover') ? 'data-hover="true"' : ''}
           ${this.hasAttribute('toggle-on-click') ? 'data-click="true"' : ''}>
        <slot></slot>

        <as-tooltip ${this.hasAttribute('show') ? 'show' : ''} 
                   ${passedAttributes}> 
          <as-text dict-key="${this.getAttribute('dict-key') ?? ''}">
            ${this.getAttribute('default-text') ?? ''}
          </as-text>
        </as-tooltip>
      </div>
    `;
    
    return template.content.cloneNode(true) as DocumentFragment;
  }

  /**
   * Handle attribute changes with a reactive approach
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    // Skip if component isn't connected yet
    if (!this.shadowRoot || !this.isConnected) return;
    
    // Skip if nothing changed
    if (oldValue === newValue) return;

    const tooltip = this.shadowRoot.querySelector('as-tooltip');
    if (!tooltip) return;
    
    // Handle specific attribute changes
    switch (name) {
      case 'show':
        if (newValue === null) {
          this.shouldBeVisible = false;
          tooltip.removeAttribute('show');
          // Clean up autoUpdate when hiding
          if (this.cleanup) {
            this.cleanup();
            this.cleanup = undefined;
          }
        } else {
          this.shouldBeVisible = true;
          tooltip.setAttribute('show', '');
          // Update position when showing
          this.updateTooltipPosition();
        }
        break;
        
      case 'placement':
        // Pass placement to tooltip component for arrow positioning
        if (newValue === null) {
          tooltip.removeAttribute('placement');
        } else {
          tooltip.setAttribute('placement', newValue);
        }
        // Update tooltip position if currently visible
        if (tooltip.hasAttribute('show')) {
          this.updateTooltipPosition();
        }
        break;

      case 'offset':
        // Update tooltip position if currently visible
        if (tooltip.hasAttribute('show')) {
          this.updateTooltipPosition();
        }
        break;
        
      case 'auto-flip':
        // Update tooltip position if currently visible since flip behavior changed
        if (tooltip.hasAttribute('show')) {
          this.updateTooltipPosition();
        }
        break;
        
      case 'no-arrow':
        if (newValue === null) {
          tooltip.removeAttribute(name);
        } else {
          tooltip.setAttribute(name, newValue ?? '');
        }
        break;
        
      case 'dict-key':
      case 'default-text':
        const textElement = tooltip.querySelector('as-text');
        if (textElement) {
          if (name === 'dict-key') {
            textElement.setAttribute('dict-key', newValue ?? '');
          } else {
            textElement.textContent = newValue ?? '';
          }
        }
        break;
        
      case 'show-on-hover':
      case 'toggle-on-click':
        // These require re-rendering to update the event handlers
        this.render();
        break;

      case 'flash':
        if (newValue !== null) {
          // Flash attribute added, trigger the flash
          this.flashTooltip();
        } else {
          // Flash attribute removed, cancel any ongoing flash
          if (this.flashTimeout) {
            clearTimeout(this.flashTimeout);
            this.flashTimeout = undefined;
          }
        }
        break;

      case 'flash-duration':
        // Duration change doesn't require immediate action
        // It will be used on the next flash
        break;
    }
  }

  /**
   * Set up event delegation
   */
  private setupEvents() {
    const wrapper = this.shadowRoot?.querySelector('.tooltip-wrapper');
    if (!wrapper) return;
    
    // Use event delegation to handle all events in one place
    wrapper.addEventListener('pointerenter', this.handleEvent);
    wrapper.addEventListener('pointerleave', this.handleEvent);
    wrapper.addEventListener('click', this.handleEvent);
  }
  
  /**
   * Unified event handler using dataset properties for behavior control
   */
  private handleEvent = (event: Event) => {
    const wrapper = event.currentTarget as HTMLElement;
    const tooltip = this.shadowRoot?.querySelector('as-tooltip');
    if (!tooltip) return;
    
    // Handle hover events
    if (wrapper.dataset.hover === 'true') {
      if (event.type === 'pointerenter') {
        this.shouldBeVisible = true;
        tooltip.setAttribute('show', '');
        // Update position when showing
        this.updateTooltipPosition();
      } else if (event.type === 'pointerleave') {
        this.shouldBeVisible = false;
        tooltip.removeAttribute('show');
        // Clean up autoUpdate when hiding
        if (this.cleanup) {
          this.cleanup();
          this.cleanup = undefined;
        }
      }
    }
    
    // Handle click events
    if (wrapper.dataset.click === 'true' && event.type === 'click') {
      if (tooltip.hasAttribute('show')) {
        this.shouldBeVisible = false;
        tooltip.removeAttribute('show');
        // Clean up autoUpdate when hiding
        if (this.cleanup) {
          this.cleanup();
          this.cleanup = undefined;
        }
      } else {
        this.shouldBeVisible = true;
        tooltip.setAttribute('show', '');
        // Update position when showing
        this.updateTooltipPosition();
      }
    }
  }

  /**
   * Main render method
   */
  private render() {
    if (!this.shadowRoot) return;
    
    // Clear existing content
    while (this.shadowRoot.firstChild) {
      this.shadowRoot.removeChild(this.shadowRoot.firstChild);
    }
    
    // Append new template
    this.shadowRoot.appendChild(this.createTemplate());
    
    // Set up events
    this.setupEvents();
  }

  connectedCallback() {
    this.render();
    
    // Show immediately if show attribute is present
    if (this.hasAttribute('show')) {
      this.shouldBeVisible = true;
      // Use setTimeout to ensure tooltip is rendered first
      setTimeout(() => this.updateTooltipPosition(), 0);
    }
  }
  
  disconnectedCallback() {
    // Clean up event listeners
    const wrapper = this.shadowRoot?.querySelector('.tooltip-wrapper');
    if (wrapper) {
      wrapper.removeEventListener('pointerenter', this.handleEvent);
      wrapper.removeEventListener('pointerleave', this.handleEvent);
      wrapper.removeEventListener('click', this.handleEvent);
    }

    // Clean up autoUpdate
    if (this.cleanup) {
      this.cleanup();
      this.cleanup = undefined;
    }

    // Clean up intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = undefined;
    }

    // Clean up flash timeout
    if (this.flashTimeout) {
      clearTimeout(this.flashTimeout);
      this.flashTimeout = undefined;
    }
  }
}

// Check if the element is already defined before registering it
if (!customElements.get('as-tooltip-wrapper')) {
  customElements.define('as-tooltip-wrapper', AsTooltipWrapper);
} else {
  console.warn("as-tooltip-wrapper already defined");
}
